import { ActivateButton } from './ActivateButton';
import { TAP_TO_SPEAK_ID } from '../core/input/activationRegistry';
import { de } from '../i18n/de';
import { metaRepo } from '../data/repositories';
import { speechService } from '../core/speech/SpeechService';
import { useAudioStatus } from '../core/speech/audioStatusStore';
import { useComposerStore } from '../core/composer/useComposer';
import { repairLabel } from '../core/speech/repairLabel';
import { useProfileStore } from '../core/profile/ProfileStore';
import { useTapToSpeak } from '../core/speech/useTapToSpeak';

/**
 * The way back after an utterance did not reach anybody (`SUR-23`).
 *
 * One target, in one place, at one size, escalating through three things to offer — because it appears at
 * the worst possible moment, and a control that moves between failures is a control that has to be found
 * while the person is already not being heard.
 *
 * `retry` is the original 3.2 behaviour: the tap is the user gesture a dwell timer could not be, so the
 * platform voice becomes available for that one utterance. `repair` rebuilds the audio path first
 * (`SPK-19`) and then says the same sentence. `reload` is the last resort and is reachable **only** after
 * a repair has visibly failed (`SHL-09`) — a reload costs a cold start and, without the draft, a sentence
 * that may have taken a minute to type, so it is earned rather than presented.
 *
 * Inside the reserved band and full width of it, so it costs no layout: the band is reserved and, in this
 * situation, not being used for candidates anybody is reading. `PredictionRow` owns the band's frame for
 * every tenant (ADR-039), which is why there is no `ContentBand` here.
 *
 * Rendered on four surfaces, which is `SUR-24` and not an accident: `visualOnly` navigates to
 * Partner-Ansicht, so an offer that existed only inside Sprechen's band was one the person it was offered
 * to could never see (C83).
 */

export function TapToSpeakOffer(): React.JSX.Element | null {
  const pending = useTapToSpeak((s) => s.pending);
  const level = useTapToSpeak((s) => s.level);
  // From the store rather than from the active adapter, because the label has to change when a caregiver
  // changes the mode — and a module-level adapter reference would not re-render this (ADR-047).
  const inputMode = useProfileStore((s) => s.settings.inputMode);
  const clear = useTapToSpeak((s) => s.clear);
  const escalate = useTapToSpeak((s) => s.escalate);
  const repairFromGesture = useAudioStatus((s) => s.repairFromGesture);

  if (pending === null) return null;

  const speakAgain = (text: string): void => {
    clear();
    void speechService.speak({ text, source: 'keyboard' });
  };

  const onPress = (): void => {
    // The explicit gesture that makes this work at all (C64) is recorded by `activate()` rather than here
    // (ADR-046). It had to move: a dwell hold and a scan selection reach this control through the registry and
    // never through a click, so a note taken in this function fired for a finger and for nothing else.
    const text = pending;

    if (level === 'reload') {
      // INV-02 permits exactly this: a reload a human asked for. Never while an utterance is in flight —
      // reloading mid-sentence is the specific thing the invariant forbids.
      if (speechService.isSpeaking()) return;
      // Save the sentence first, and reload whether or not the write succeeded: a failed write must not
      // leave the person holding a broken app because we could not preserve their text.
      void metaRepo
        .update({ draft: useComposerStore.getState().state.text })
        .catch((err: unknown) => console.error('[audio] could not save the draft', err))
        .finally(() => window.location.reload());
      return;
    }

    if (level === 'retry') {
      speakAgain(text);
      return;
    }

    void repairFromGesture().then((ok) => {
      // The re-speak *is* the feedback. A "working…" state between the two would be a second thing to
      // interpret at the moment the person has least attention for one.
      if (ok) speakAgain(text);
      else escalate();
    });
  };

  return (
    <div
      data-level={level}
      style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
    >
      {/*
       * An `ActivateButton` rather than a plain button (ADR-046), which is what puts it in the activation
       * registry and therefore in the scan order and the hit test. It was the exception `CON-01` says does not
       * exist, and the cost was that a switch user could not reach the one control that returns their voice.
       *
       * What that costs in the other direction, deliberately: in a timed mode the pointer click is discarded
       * (C62), so this is no longer an immediate tap in dwell or scanning — it is a hold or a scan selection,
       * like every other target.
       */}
      <ActivateButton
        id={TAP_TO_SPEAK_ID}
        testId="tap-to-speak"
        onActivate={onPress}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: 'var(--min-target)',
          padding: '8px 16px',
          fontSize: 'var(--font-button)',
          fontWeight: 700,
          // Colour is not the only signal — the border and the weight carry it too (§5.2). The last resort
          // is drawn in the danger colour because it is the one step that costs something.
          color: level === 'reload' ? 'var(--danger-fg)' : 'var(--accent-fg)',
          background: level === 'reload' ? 'var(--danger)' : 'var(--accent)',
          border: '4px solid var(--fg)',
          borderRadius: 'var(--radius)',
        }}
      >
        {repairLabel(level, inputMode)}
      </ActivateButton>
      {level === 'reload' ? (
        <span data-testid="tap-to-speak-hint" style={{ fontSize: 'var(--font-body)', textAlign: 'center' }}>
          {de.audioReloadHint}
        </span>
      ) : null}
    </div>
  );
}
